/**
 * Generates a sample JSON object from a JSON Schema definition
 */
export function generateSampleJson(schema: any): any {
  if (!schema || typeof schema !== 'object') {
    return {};
  }

  const type = schema.type;

  // Handle different types
  switch (type) {
    case 'object':
      return generateObjectSample(schema);
    case 'array':
      return generateArraySample(schema);
    case 'string':
      return generateStringSample(schema);
    case 'number':
    case 'integer':
      return generateNumberSample(schema);
    case 'boolean':
      return schema.default !== undefined ? schema.default : false;
    case 'null':
      return null;
    default:
      // If no type specified but has properties, treat as object
      if (schema.properties) {
        return generateObjectSample(schema);
      }
      return null;
  }
}

function generateObjectSample(schema: any): any {
  const obj: any = {};
  
  if (schema.properties) {
    for (const [key, propSchema] of Object.entries(schema.properties)) {
      obj[key] = generateSampleJson(propSchema);
    }
  }
  
  return obj;
}

function generateArraySample(schema: any): any[] {
  if (schema.items) {
    // Generate one sample item
    return [generateSampleJson(schema.items)];
  }
  return [];
}

function generateStringSample(schema: any): string {
  // Use example if available
  if (schema.example !== undefined) {
    return schema.example;
  }
  
  // Use default if available
  if (schema.default !== undefined) {
    return schema.default;
  }
  
  // Use description as placeholder
  if (schema.description) {
    return schema.description;
  }
  
  // Use enum first value if available
  if (schema.enum && schema.enum.length > 0) {
    return schema.enum[0];
  }
  
  return 'string';
}

function generateNumberSample(schema: any): number {
  // Use example if available
  if (schema.example !== undefined) {
    return schema.example;
  }
  
  // Use default if available
  if (schema.default !== undefined) {
    return schema.default;
  }
  
  // Use minimum if available
  if (schema.minimum !== undefined) {
    return schema.minimum;
  }
  
  // Use enum first value if available
  if (schema.enum && schema.enum.length > 0) {
    return schema.enum[0];
  }
  
  return 0;
}

/**
 * Generates a sample JSON string (formatted) from a JSON Schema definition
 */
export function generateSampleJsonString(schema: any): string {
  try {
    const sample = generateSampleJson(schema);
    return JSON.stringify(sample, null, 2);
  } catch (error) {
    console.error('Error generating sample JSON:', error);
    return '{}';
  }
}
